import { RnaComplexProps } from "../App";
import BasePair, { getBasePairType } from "../components/app_specific/BasePair";
import { Nucleotide } from "../components/app_specific/Nucleotide";
import { RnaComplex, isRelevantBasePairKeySetInPair } from "../components/app_specific/RnaComplex";
import { RnaMolecule } from "../components/app_specific/RnaMolecule";
import { TextAnnotation } from "../components/app_specific/TextAnnotation";
import Color, { toCSS, BLACK } from "../data_structures/Color";
import Font from "../data_structures/Font";
import { Vector2D, add } from "../data_structures/Vector2D";
import { DEFAULT_STROKE_WIDTH } from "../utils/Constants";
import { OutputFileWriter } from "./OutputUI";

type CssClassForJson = {
  ["font-family"]? : string,
  ["font-size"]? : string,
  ["font-weight"]? : string,
  ["font-style"]? : string,
  ["stroke-width"]? : string,
  stroke? : string,
  name : string
};

type BasePairForJsonPerRnaMolecule = {
  basePairType : BasePair.Type,
  classes : Array<string>,
  residueIndex1 : number,
  residueIndex2 : number,
  points? : Array<Vector2D>
};

type BasePairForJsonPerRnaComplex = BasePairForJsonPerRnaMolecule & {
  rnaMoleculeName1 : string,
  rnaMoleculeName2 : string
};

type LabelForJson = {
  labelContent? : {
    classes : Array<string>,
    label : string,
    x : number,
    y : number
  },
  labelLine? : {
    classes : Array<string>,
    points : Array<Vector2D>
  },
  residueIndex : number
};

type SequenceConnectorForJson = {
  breakpoints : Array<Vector2D>,
  color? : string,
  strokeWidth? : number,
  opacity? : number,
  dashArray? : string,
  curvature? : number,
  showDirectionArrow? : boolean,
  showBreakpoints? : boolean,
  arrowColor? : string,
  arrowShape? : Nucleotide.SequenceConnector.ArrowShape,
  arrowPosition? : number,
  arrowPositionRight? : number,
  arrowSize? : number,
  lockedBreakpoints? : number[],
  breakpointGroups? : Nucleotide.SequenceConnector.BreakpointGroup[],
  targetMoleculeName? : string,
  targetNucleotideIndex? : number,
  targetComplexIndex? : number,
  deleted? : boolean,
  isORF? : boolean
};

type TextAnnotationForJson = {
  id : string,
  content : string,
  x : number,
  y : number,
  font? : {
    family : string,
    size : string,
    weight : string,
    style : string
  },
  color? : string,
  strokeColor? : string,
  strokeWidth? : number,
  strokeOpacity? : number,
  opacity? : number,
  rotation? : number,
  backgroundColor? : string,
  padding? : number,
  borderColor? : string,
  borderWidth? : number,
  borderRadius? : number
};

export const jsonFileWriter : OutputFileWriter = (rnaComplexProps : RnaComplexProps) => {
  let fontCssClasses : Array<CssClassForJson> = [];
  let strokeCssClasses : Array<CssClassForJson> = [];
  let labelContentCssClasses : Record<number, Array<string>> = {};
  let labelLineCssClasses : Record<number, Array<string>> = {};
  let nucleotideCssClasses : Record<number, Array<string>> = {};
  let basePairsCssClasses : Record<number, Array<string>> = {};
  let fontCssClassName : string = "";
  function handleFontCss(font : Font) {
    let fontCssClassIndex = fontCssClasses.findIndex(cssClassForJson => cssClassForJson["font-family"] === font.family && cssClassForJson["font-size"] === `${font.size}` && cssClassForJson["font-weight"] === font.weight && cssClassForJson["font-style"] === font.style);
    if (fontCssClassIndex === -1) {
      fontCssClassName = `font#${fontCssClasses.length}`;
      fontCssClasses.push({
        name : fontCssClassName,
        ["font-family"] : font.family,
        ["font-size"] : `${font.size}`,
        ["font-weight"] : font.weight,
        ["font-style"] : font.style
      });
    } else {
      fontCssClassName = `font#${fontCssClassIndex}`;
    }
  };
  let strokeCssClassName : string = "";
  function handleStrokeCss(stroke : {strokeWidth : number | string, stroke : Color}) {
    let strokeAsText = toCSS(stroke.stroke);
    let strokeCssClassIndex = strokeCssClasses.findIndex(cssClassForJson => cssClassForJson["stroke-width"] === `${stroke.strokeWidth}` && cssClassForJson.stroke === strokeAsText);
    if (strokeCssClassIndex === -1) {
      strokeCssClassName = `stroke#${strokeCssClasses.length}`;
      strokeCssClasses.push({
        name : strokeCssClassName,
        ["stroke-width"] : `${stroke.strokeWidth}`,
        stroke : strokeAsText
      });
    } else {
      strokeCssClassName = `stroke#${strokeCssClassIndex}`;
    }
  };
  const flattenedRnaComplexProps = Object.values(rnaComplexProps);
  flattenedRnaComplexProps.forEach(singularRnaComplexProps => {
    const flattenedRnaMoleculeProps = Object.values(singularRnaComplexProps.rnaMoleculeProps);
    flattenedRnaMoleculeProps.forEach((singularRnaMoleculeProps : RnaMolecule.ExternalProps) => {
      const flattenedNucleotideProps = Object.entries(singularRnaMoleculeProps.nucleotideProps);
      flattenedNucleotideProps.forEach(([
        nucleotideIndexAsString,
        singularNucleotideProps,
      ]) => {
        let nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
        handleFontCss(singularNucleotideProps.font ?? Font.DEFAULT);
        nucleotideCssClasses[nucleotideIndex] = [
          "text-" + toCSS(singularNucleotideProps.color ?? BLACK),
          fontCssClassName
        ];
        let labelContentProps = singularNucleotideProps.labelContentProps;
        if (labelContentProps !== undefined) {
          handleFontCss(labelContentProps.font ?? Font.DEFAULT);
          labelContentCssClasses[nucleotideIndex] = [
            "text-" + toCSS(labelContentProps.color ?? BLACK),
            fontCssClassName
          ];
        }
        let labelLineProps = singularNucleotideProps.labelLineProps;
        if (labelLineProps !== undefined) {
          handleStrokeCss({
            stroke : labelLineProps.color ?? BLACK,
            strokeWidth : labelLineProps.strokeWidth ?? DEFAULT_STROKE_WIDTH
          });
          labelLineCssClasses[nucleotideIndex] = [
            strokeCssClassName
          ];
        }
      });
    });
  });
  return JSON.stringify({
    classes : fontCssClasses.concat(strokeCssClasses),
    rnaComplexes : flattenedRnaComplexProps.map((singularRnaComplexProps : RnaComplex.ExternalProps) => {
      const flattenedRnaMoleculeProps = Object.entries(singularRnaComplexProps.rnaMoleculeProps);
      const basePairsPerRnaComplex = singularRnaComplexProps.basePairs;
      const outputBasePairsPerRnaComplex = new Array<BasePairForJsonPerRnaComplex>();
      return {
        name : singularRnaComplexProps.name,
        rnaMolecules : flattenedRnaMoleculeProps.map(function([
          rnaMoleculeName,
          singularRnaMoleculeProps
        ]) {
          const outputBasePairsPerRnaMolecule = new Array<BasePairForJsonPerRnaMolecule>();
          const basePairsPerRnaMolecule = basePairsPerRnaComplex[rnaMoleculeName] || {};
          const flattenedBasePairsPerRnaMolecule = Object.entries(basePairsPerRnaMolecule);
          for (const [nucleotideIndexAsString, basePairsPerNucleotide] of flattenedBasePairsPerRnaMolecule) {
            for (const basePairPerNucleotide of basePairsPerNucleotide) {
              const nucleotideIndex = Number.parseInt(nucleotideIndexAsString);
              if (!isRelevantBasePairKeySetInPair(
                {
                  rnaMoleculeName,
                  nucleotideIndex
                },
                basePairPerNucleotide
              )) {
                continue;
              }
              const basePairType = basePairPerNucleotide.basePairType ?? getBasePairType(
                singularRnaMoleculeProps.nucleotideProps[nucleotideIndex].symbol,
                singularRnaComplexProps.rnaMoleculeProps[basePairPerNucleotide.rnaMoleculeName].nucleotideProps[basePairPerNucleotide.nucleotideIndex].symbol
              );
              handleStrokeCss({
                strokeWidth : basePairPerNucleotide.strokeWidth ?? DEFAULT_STROKE_WIDTH,
                stroke : basePairPerNucleotide.color ?? BLACK
              });
              basePairsCssClasses[nucleotideIndex] = [
                strokeCssClassName
              ];
              const basePair = {
                basePairType,
                classes : basePairsCssClasses[nucleotideIndex],
                residueIndex1 : singularRnaMoleculeProps.firstNucleotideIndex + nucleotideIndex,
                residueIndex2 : singularRnaMoleculeProps.firstNucleotideIndex + basePairPerNucleotide.nucleotideIndex,
                points : basePairPerNucleotide.points
              };
              let rnaMoleculeName1 = rnaMoleculeName;
              let rnaMoleculeName2 = basePairPerNucleotide.rnaMoleculeName;
              if (BasePair.isRedundantLeontisWesthofType(basePair.basePairType)) {
                basePair.basePairType = BasePair.reverseDirectedTypeMap[basePair.basePairType];
                const tempRnaMoleculeName = rnaMoleculeName1;
                rnaMoleculeName1 = rnaMoleculeName2;
                rnaMoleculeName2 = tempRnaMoleculeName;
                const tempResidueIndex = basePair.residueIndex1;
                basePair.residueIndex1 = basePair.residueIndex2;
                basePair.residueIndex2 = tempResidueIndex;
              }
              if (rnaMoleculeName1 === rnaMoleculeName2) {
                outputBasePairsPerRnaMolecule.push(basePair);
              } else {
                outputBasePairsPerRnaComplex.push({
                  ...basePair,
                  rnaMoleculeName1,
                  rnaMoleculeName2
                });
              }
            }
          };
          let labels = new Array<LabelForJson>();
          const flattenedNucleotideProps = Object.entries(singularRnaMoleculeProps.nucleotideProps).map(function([
            nucleotideIndexAsString,
            singularNucleotideProps
          ]) {
            return {
              nucleotideIndex : Number.parseInt(nucleotideIndexAsString),
              singularNucleotideProps
            };
          });
          flattenedNucleotideProps.forEach(function({
            nucleotideIndex,
            singularNucleotideProps
          }) {
            let labelContentProps = singularNucleotideProps.labelContentProps;
            let labelLineProps = singularNucleotideProps.labelLineProps;
            if (labelContentProps !== undefined || labelLineProps !== undefined) {
              let label : LabelForJson = {
                residueIndex : nucleotideIndex + singularRnaMoleculeProps.firstNucleotideIndex
              };
              if (labelContentProps !== undefined) {
                label.labelContent = {
                  classes : labelContentCssClasses[nucleotideIndex],
                  label : labelContentProps.content,
                  x : labelContentProps.x + singularNucleotideProps.x,
                  y : -(labelContentProps.y + singularNucleotideProps.y)
                };
              }
              if (labelLineProps !== undefined) {
                label.labelLine = {
                  classes : labelLineCssClasses[nucleotideIndex],
                  points : labelLineProps.points.map(function(point) {
                    const pointSum = add(
                      point,
                      singularNucleotideProps
                    );
                    pointSum.y = -pointSum.y;
                    return pointSum;
                  })
                };
              }
              labels.push(label);
            }
          });
          return {
            name : rnaMoleculeName,
            basePairs : outputBasePairsPerRnaMolecule,
            labels,
            sequence : flattenedNucleotideProps.map(function({
              nucleotideIndex,
              singularNucleotideProps
            }) {
              const sequenceEntry : any = {
                classes : nucleotideCssClasses[nucleotideIndex],
                residueIndex : nucleotideIndex + singularRnaMoleculeProps.firstNucleotideIndex,
                residueName : singularNucleotideProps.symbol,
                x : singularNucleotideProps.x,
                y : -singularNucleotideProps.y
              };
              // Export sequence connector if present
              const connector = singularNucleotideProps.sequenceConnectorToNext;
              if (connector) {
                const connectorForJson : SequenceConnectorForJson = {
                  breakpoints : connector.breakpoints?.map(bp => ({ x: bp.x, y: -bp.y })) ?? []
                };
                if (connector.color) connectorForJson.color = toCSS(connector.color);
                if (connector.strokeWidth !== undefined) connectorForJson.strokeWidth = connector.strokeWidth;
                if (connector.opacity !== undefined) connectorForJson.opacity = connector.opacity;
                if (connector.dashArray) connectorForJson.dashArray = connector.dashArray;
                if (connector.curvature !== undefined) connectorForJson.curvature = connector.curvature;
                if (connector.showDirectionArrow !== undefined) connectorForJson.showDirectionArrow = connector.showDirectionArrow;
                if (connector.showBreakpoints !== undefined) connectorForJson.showBreakpoints = connector.showBreakpoints;
                if (connector.arrowColor) connectorForJson.arrowColor = toCSS(connector.arrowColor);
                if (connector.arrowShape) connectorForJson.arrowShape = connector.arrowShape;
                if (connector.arrowPosition !== undefined) connectorForJson.arrowPosition = connector.arrowPosition;
                if (connector.arrowPositionRight !== undefined) connectorForJson.arrowPositionRight = connector.arrowPositionRight;
                if (connector.arrowSize !== undefined) connectorForJson.arrowSize = connector.arrowSize;
                if (connector.lockedBreakpoints?.length) connectorForJson.lockedBreakpoints = connector.lockedBreakpoints;
                if (connector.breakpointGroups?.length) connectorForJson.breakpointGroups = connector.breakpointGroups;
                if (connector.targetMoleculeName) connectorForJson.targetMoleculeName = connector.targetMoleculeName;
                if (connector.targetNucleotideIndex !== undefined) connectorForJson.targetNucleotideIndex = connector.targetNucleotideIndex;
                if (connector.targetComplexIndex !== undefined) connectorForJson.targetComplexIndex = connector.targetComplexIndex;
                if (connector.deleted !== undefined) connectorForJson.deleted = connector.deleted;
                if (connector.isORF !== undefined) connectorForJson.isORF = connector.isORF;
                sequenceEntry.sequenceConnector = connectorForJson;
              }
              return sequenceEntry;
            })
          };
        }),
        basePairs : outputBasePairsPerRnaComplex,
        textAnnotations : singularRnaComplexProps.textAnnotations ? 
          Object.entries(singularRnaComplexProps.textAnnotations).map(([id, annotation]) => {
            const annotationForJson : TextAnnotationForJson = {
              id : annotation.id,
              content : annotation.content,
              x : annotation.x,
              y : -annotation.y
            };
            if (annotation.font) {
              annotationForJson.font = {
                family : annotation.font.family,
                size : String(annotation.font.size),
                weight : annotation.font.weight,
                style : annotation.font.style
              };
            }
            if (annotation.color) annotationForJson.color = toCSS(annotation.color);
            if (annotation.strokeColor) annotationForJson.strokeColor = toCSS(annotation.strokeColor);
            if (annotation.strokeWidth !== undefined) annotationForJson.strokeWidth = annotation.strokeWidth;
            if (annotation.strokeOpacity !== undefined) annotationForJson.strokeOpacity = annotation.strokeOpacity;
            if (annotation.opacity !== undefined) annotationForJson.opacity = annotation.opacity;
            if (annotation.rotation !== undefined) annotationForJson.rotation = annotation.rotation;
            if (annotation.backgroundColor) annotationForJson.backgroundColor = toCSS(annotation.backgroundColor);
            if (annotation.padding !== undefined) annotationForJson.padding = annotation.padding;
            if (annotation.borderColor) annotationForJson.borderColor = toCSS(annotation.borderColor);
            if (annotation.borderWidth !== undefined) annotationForJson.borderWidth = annotation.borderWidth;
            if (annotation.borderRadius !== undefined) annotationForJson.borderRadius = annotation.borderRadius;
            return annotationForJson;
          }) : []
      };
    })
  });
}